import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, FileText, ShoppingCart, Users, Settings, LogOut, Receipt, Mail, Calendar, UserCog, Landmark, X, Palette, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import NotificationBell from "@/components/NotificationBell";

const adminNavItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord" },
  { to: "/devis", icon: FileText, label: "Devis" },
  { to: "/commandes", icon: ShoppingCart, label: "Bons de Commande" },
  { to: "/factures", icon: Receipt, label: "Factures" },
  { to: "/interventions", icon: Calendar, label: "Interventions" },
  { to: "/clients", icon: Users, label: "Clients" },
  { to: "/messagerie", icon: Mail, label: "Messagerie" },
  { to: "/utilisateurs", icon: UserCog, label: "Utilisateurs" },
  { to: "/comptabilite", icon: Landmark, label: "Comptabilité" },
  { to: "/modeles", icon: Palette, label: "Modèles" },
  { to: "/settings", icon: Settings, label: "Paramètres" },
];

const webmasterOnlyItems = [
  { to: "/webmaster", icon: Shield, label: "WebMaster" },
];

const clientNavItems = [
  { to: "/espace-client", icon: LayoutDashboard, label: "Mon Espace" },
];

export default function Sidebar({ onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "webmaster";
  const isWebmaster = user?.role === "webmaster";
  const navItems = isAdmin
    ? [...adminNavItems, ...(isWebmaster ? webmasterOnlyItems : [])]
    : clientNavItems;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside className="w-64 bg-slate-900 text-white flex flex-col h-full shrink-0" data-testid="sidebar">
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/jvc-logo.png" alt="JVC" className="w-11 h-11 rounded-md object-contain bg-white p-0.5" />
            <div>
              <h1 className="font-barlow text-lg font-bold tracking-tight uppercase">SAS J.V.C</h1>
              <p className="text-[10px] text-slate-400 leading-tight">Structure métallique & Soudure</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && <NotificationBell />}
            {onClose && (
              <button onClick={onClose} className="lg:hidden p-1 rounded hover:bg-slate-800 text-slate-400" data-testid="sidebar-close-btn">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
      <Separator className="bg-slate-700/50" />
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`nav-${item.to.slice(1)}`}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-orange-500/10 text-orange-400" : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
              }`
            }
          >
            <item.icon className="w-[18px] h-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <Separator className="bg-slate-700/50" />
      <div className="p-3">
        <div className="flex items-center gap-2.5 px-3 py-2">
          <Avatar className="w-8 h-8">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-slate-700 text-xs">{user?.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <div className="flex items-center gap-1.5">
              <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
              {isWebmaster && <Badge className="bg-purple-500/20 text-purple-400 text-[9px] px-1 py-0 border-0 h-4">WebMaster</Badge>}
              {isAdmin && !isWebmaster && <Badge className="bg-orange-500/20 text-orange-400 text-[9px] px-1 py-0 border-0 h-4">Admin</Badge>}
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 mt-1"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Déconnexion
        </Button>
        <p className="text-[9px] text-slate-600 text-center mt-3">CopyRight by De La Rosa Design</p>
      </div>
    </aside>
  );
}
